<template>
    <div>
        <FullscreenLoading v-if="isLoading" />

        <div class="row mt-3" v-if="formValues">
            <div class="col-6">
                <div class="card h-100">
                    <div class="card-header pb-3" v-if="!embed">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="h4">{{ $t('emailtemplate.edit_title') }}</span>
                        </div>
                    </div>

                    <div class="card-body">
                        <form @submit.prevent="save">
                            <ValidationObserver v-slot="{ invalid }" ref="emailTemplateForm">
                                <ValidationProvider :name="$t('general.subject')" rules="required" v-slot="{ errors }">
                                    <div class="form-group row">
                                        <label class="form-control-label col-12">{{ $t('general.subject') }}*</label>
                                        <div class="col-12">
                                            <input
                                                :disabled="isLoading"
                                                type="text"
                                                class="form-control form-control-sm"
                                                v-model="formValues.subject"
                                                :class="{ 'is-invalid': errors.length }"
                                            />

                                            <div v-if="errors.length" class="invalid-feedback">{{ errors[0] }}</div>
                                        </div>
                                    </div>
                                </ValidationProvider>

                                <ValidationProvider :name="$t('emailtemplate.message_label')" :rules="messageRules" v-slot="{ errors }">
                                    <div class="form-group row">
                                        <label class="form-control-label col-12">{{ $t('emailtemplate.message_label') }}*</label>
                                        <div class="col-12">
                                            <Editor
                                                v-model="formValues.message"
                                                :disabled="isLoading"
                                                :merge-fields="mergeFields"
                                                :class="{ 'is-invalid': errors.length }"
                                            />

                                            <div v-if="errors.length" class="invalid-feedback">{{ errors[0] }}</div>
                                        </div>

                                        <div class="col-12">
                                            <small class="d-block mt-2">{{ $t('emailtemplate.mergefield_description') }}</small>
                                        </div>
                                    </div>
                                </ValidationProvider>

                                <button class="btn btn-primary btn-sm float-right" type="submit" :disabled="invalid">
                                    {{ $t('general.save') }}
                                </button>
                            </ValidationObserver>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-6">
                <div class="card h-100">
                    <div class="card-header pb-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="h4">{{ $t('emailtemplate.preview_title') }}</span>
                        </div>
                    </div>
                    <div class="card-body">
                        <EmailTemplatePreview :message="formValues.message" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>

    import { computed, inject, onMounted, ref } from 'vue';
    import { useRoute } from 'vue-router/composables';
    import { notifyError, notifySuccess } from '../../utils/notify';
    import FullscreenLoading from '../../components/FullscreenLoading.vue';
    import Editor from './Editor.vue';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import EmailTemplatePreview from '../../components/EmailTemplatePreview.vue';
    import i18n from '../../utils/locale';

    const route = useRoute();
    const http = inject('http');

    const isLoading = ref(false);
    const formValues = ref(null);
    const mergeFields = ref([]);

    const emailTemplateForm = ref(null);

    const embed = computed(() => {
        return window.location.search.includes('embed=true');
    });

    const messageRules = computed(() => {
        return {
            required: true,
            contains: mergeFields.value.filter(mergeField => mergeField.required).map(mergeField => `[[${mergeField.name}]]`),
        };
    });

    onMounted(() => {
        load(route.params.type);
    });

    async function load(type) {
        isLoading.value = true;

        try {
            const response = await http.get(`/api/email-templates/${type}`);

            mergeFields.value = response.data.merge_fields;
            formValues.value = {
                ...response.data,
            };
        } catch (exception) {
            notifyError(exception);
        } finally {
            isLoading.value = false;
        }
    }

    async function save() {
        isLoading.value = true;

        try {
            await http.put(`/api/email-templates/${route.params.type}`, formValues.value);
            notifySuccess(i18n.t('emailtemplate.saved'));
        } catch (exception) {
            emailTemplateForm.value.setErrors(exception?.response?.data?.errors);

            notifyError(exception);
        } finally {
            isLoading.value = false;
        }
    }

</script>
